import { Box, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { OverviewLiveSignals } from "../models/types";
import { EquipmentBox } from "./EquipmentBox";
import { EquipmentLegend } from "./EquipmentLegend";
import {
	CLIP_EMPTY_THRESHOLD,
	CLIP_LOW_THRESHOLD,
	autoCleanTone,
	clipLowTone,
	driveEnabledTone,
	estopTone,
	hydTempTone,
	robotTone,
} from "./equipmentTones";

interface Props {
	live: OverviewLiveSignals | null;
	loading?: boolean;
}

const BoxSkeletonRow = ({ count }: { count: number }) => (
	<Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.4 }}>
		{Array.from({ length: count }).map((_, i) => (
			<Skeleton key={i} variant="rounded" width={36} height={42} />
		))}
	</Stack>
);

const SkeletonGroup = ({ count }: { count: number }) => (
	<Box sx={{ minWidth: 0 }}>
		<Skeleton variant="text" width={70} height={16} sx={{ mb: 0.4 }} />
		<BoxSkeletonRow count={count} />
	</Box>
);

const liveGroupColumnSx = {
	minWidth: 0,
	display: "flex",
	flexDirection: "column",
	gap: 1.1,
	pt: 0.5,
} as const;

const EquipmentSkeleton = () => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
		}}
	>
		<Box sx={{ display: "flex", alignItems: "stretch", gap: 1, flexGrow: 1 }}>
			<Box sx={{ flex: 1.7, pt: 0.5 }}>
				<SkeletonGroup count={6} />
			</Box>
			<Divider orientation="vertical" flexItem />
			<Box sx={{ flex: 2.2, pt: 0.5 }}>
				<SkeletonGroup count={8} />
			</Box>
			<Divider orientation="vertical" flexItem />
			<Box
				sx={{
					flex: 1.8,
					display: "flex",
					flexDirection: "column",
					gap: 1.1,
					pt: 0.5,
				}}
			>
				<SkeletonGroup count={4} />
				<SkeletonGroup count={2} />
			</Box>
			<Divider orientation="vertical" flexItem />
			<Box
				sx={{
					flex: 2.5,
					display: "flex",
					flexDirection: "column",
					gap: 1.1,
					pt: 0.5,
				}}
			>
				<SkeletonGroup count={5} />
				<SkeletonGroup count={3} />
			</Box>
		</Box>
	</Box>
);

const Group = ({
	label,
	children,
	columns,
}: {
	label: string;
	children: React.ReactNode;
	columns?: number;
}) => (
	<Box sx={{ minWidth: 0 }}>
		<Typography
			variant="subtitle2"
			sx={{ fontWeight: 600, mb: 0.4, fontSize: "0.75rem" }}
		>
			{label}
		</Typography>
		{columns ? (
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: `repeat(${columns}, max-content)`,
					gap: 0.4,
				}}
			>
				{children}
			</Box>
		) : (
			<Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.4, rowGap: 0.4 }}>
				{children}
			</Stack>
		)}
	</Box>
);

export const EquipmentStatusPanel = ({ live, loading }: Props) => {
	if (loading) return <EquipmentSkeleton />;
	if (!live) {
		return (
			<Typography variant="body2" color="text.secondary">
				No live signals available.
			</Typography>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "stretch", gap: 1, flexGrow: 1 }}>
				<Box
					sx={{
						...liveGroupColumnSx,
						flex: 1.7,
					}}
				>
					<Group label="Robots" columns={3}>
						<EquipmentBox
							label="R1"
							tone={robotTone(live.R1_E1, live.R1Alarm)}
							tooltip={live.R1Alarm === 1 ? "East 1 — ALARM" : "East 1"}
						/>
						<EquipmentBox
							label="R2"
							tone={robotTone(live.R2_E2, live.R2Alarm)}
							tooltip={live.R2Alarm === 1 ? "East 2 — ALARM" : "East 2"}
						/>
						<EquipmentBox
							label="R3"
							tone={robotTone(live.R3_E3, live.R3Alarm)}
							tooltip={live.R3Alarm === 1 ? "East 3 — ALARM" : "East 3"}
						/>
						<EquipmentBox
							label="R4"
							tone={robotTone(live.R4_W1, live.R4Alarm)}
							tooltip={live.R4Alarm === 1 ? "West 1 — ALARM" : "West 1"}
						/>
						<EquipmentBox
							label="R5"
							tone={robotTone(live.R5_W2, live.R5Alarm)}
							tooltip={live.R5Alarm === 1 ? "West 2 — ALARM" : "West 2"}
						/>
						<EquipmentBox
							label="R6"
							tone={robotTone(live.R6_W3, live.R6Alarm)}
							tooltip={live.R6Alarm === 1 ? "West 3 — ALARM" : "West 3"}
						/>
					</Group>
				</Box>

				<Divider orientation="vertical" flexItem />

				<Box
					sx={{
						...liveGroupColumnSx,
						flex: 2.2,
					}}
				>
					<Box sx={{ minWidth: 0 }}>
						<Typography
							variant="subtitle2"
							sx={{ fontWeight: 600, mb: 0.4, fontSize: "0.75rem" }}
						>
							Gates
						</Typography>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(4, max-content)",
								columnGap: 0.4,
								rowGap: 0.4,
								justifyContent: "start",
							}}
						>
							<EquipmentBox label="G1E" tone={estopTone(live.EastGate1)} />
							<EquipmentBox label="G2E" tone={estopTone(live.EastGate2)} />
							<EquipmentBox label="G3E" tone={estopTone(live.EastGate3)} />
							<EquipmentBox label="G4E" tone={estopTone(live.EastGate4)} />
							<EquipmentBox label="G1W" tone={estopTone(live.WestGate1)} />
							<EquipmentBox label="G2W" tone={estopTone(live.WestGate2)} />
							<EquipmentBox label="G3W" tone={estopTone(live.WestGate3)} />
							<EquipmentBox label="G4W" tone={estopTone(live.WestGate4)} />
						</Box>
					</Box>
				</Box>

				<Divider orientation="vertical" flexItem />

				<Box
					sx={{
						...liveGroupColumnSx,
						flex: 1.8,
					}}
				>
					<Group label="Handling">
						<EquipmentBox
							label="Destack E"
							tone={estopTone(live.DestackerE)}
							tooltip="Destacker East"
						/>
						<EquipmentBox
							label="Destack W"
							tone={estopTone(live.DestackerW)}
							tooltip="Destacker West"
						/>
						<EquipmentBox label="Stacker" tone={estopTone(live.Stacker)} />
					</Group>

					<Group label="Stations">
						<EquipmentBox
							label="Station E"
							tone={estopTone(live.StationE)}
							tooltip="Weld station East"
						/>
						<EquipmentBox
							label="Station W"
							tone={estopTone(live.StationW)}
							tooltip="Weld station West"
						/>
					</Group>
				</Box>

				<Divider orientation="vertical" flexItem />

				<Box
					sx={{
						...liveGroupColumnSx,
						flex: 2.5,
					}}
				>
					<Group label="System">
						<EquipmentBox
							label="Hyd"
							tone={estopTone(live.HydEstop, "highTriggered")}
							tooltip="Hydraulic E-Stop"
						/>
						<EquipmentBox
							label="E-Stop"
							tone={estopTone(live.SystemEstop, "highTriggered")}
							tooltip="System E-Stop"
						/>
						<EquipmentBox
							label="Safety"
							tone={estopTone(live.SystemSafetyEstop)}
							tooltip="System Safety E-Stop"
						/>
						<EquipmentBox
							label="Drive"
							tone={driveEnabledTone(live.DriveEnabled)}
							tooltip={
								live.DriveEnabled === null || live.DriveEnabled === undefined
									? "Servo drive — no data"
									: live.DriveEnabled
										? "Servo drive enabled"
										: "Servo drive disabled"
							}
						/>
						<EquipmentBox
							label="Auto-clean"
							tone={autoCleanTone(live.AutoCleanSet)}
							tooltip={
								live.AutoCleanSet === 1
									? "Auto cleaning cycle active"
									: "Auto cleaning cycle inactive"
							}
						/>
					</Group>

					<Group label="Sensors">
						<EquipmentBox
							label="Hyd Temp"
							tone={hydTempTone(live.HydHiTempAlarm, live.HydLoTempAlarm)}
							value={
								live.HydOilTemp != null
									? `${Math.round(live.HydOilTemp)}°`
									: "?"
							}
							tooltip={
								live.HydOilTemp != null
									? `Hyd oil temp ${live.HydOilTemp}°F` +
										(live.HydHiTempAlarm === 1
											? " — HI alarm"
											: live.HydLoTempAlarm === 1
												? " — LO alarm"
												: "")
									: "Hyd oil temp — no data"
							}
						/>
						<EquipmentBox
							label="Recirc"
							tone={hydTempTone(live.HydHiTempAlarm, live.HydLoTempAlarm)}
							value={
								live.HydRecircTemp != null
									? `${Math.round(live.HydRecircTemp)}°`
									: "?"
							}
							tooltip={
								live.HydRecircTemp != null
									? `Hyd recirc temp ${live.HydRecircTemp}°F` +
										(live.HydHiTempAlarm === 1
											? " — HI alarm"
											: live.HydLoTempAlarm === 1
												? " — LO alarm"
												: "")
									: "Hyd recirc temp — no data"
							}
						/>
						<EquipmentBox
							label="Clip E"
							tone={clipLowTone(live.ClipELowCount)}
							value={live.ClipELowCount != null ? live.ClipELowCount : "?"}
							tooltip={
								live.ClipELowCount != null
									? `Clip East — ${live.ClipELowCount} remaining` +
										(live.ClipELowCount <= CLIP_EMPTY_THRESHOLD
											? " — EMPTY, refill"
											: live.ClipELowCount < CLIP_LOW_THRESHOLD
												? " — running low"
												: "")
									: "Clip East — no data"
							}
						/>
						<EquipmentBox
							label="Clip W"
							tone={clipLowTone(live.ClipWLowCount)}
							value={live.ClipWLowCount != null ? live.ClipWLowCount : "?"}
							tooltip={
								live.ClipWLowCount != null
									? `Clip West — ${live.ClipWLowCount} remaining` +
										(live.ClipWLowCount <= CLIP_EMPTY_THRESHOLD
											? " — EMPTY, refill"
											: live.ClipWLowCount < CLIP_LOW_THRESHOLD
												? " — running low"
												: "")
									: "Clip West — no data"
							}
						/>
					</Group>
				</Box>
			</Box>

			<EquipmentLegend />
		</Box>
	);
};
